"use client";

import { useCallback, useEffect, useRef } from "react";
import styles from "./ProfileTabSecurity.module.css";

/**
 * MfaCodeInput — 6 osobnych pól cyfrowych na kod TOTP (analog do login MFA
 * challenge w Imperial admin panel).
 *
 * Interakcje:
 *  - Wpis cyfry -> auto-focus następnego pola
 *  - Backspace na pustym polu -> przeskok wstecz + kasowanie
 *  - Arrow left/right -> nawigacja między polami
 *  - Paste -> rozdziela 6-cyfrowy string na pola i auto-submit gdy pełny
 *  - Wypełnienie 6-tej cyfry -> auto-submit (parent callback)
 *
 * State kontroluje parent przez `value: string` (0-6 cyfr) + `onChange`.
 * `disabled` blokuje wszystkie inputy podczas submit'u. `hasError` dodaje
 * czerwoną obwódkę (nie pokazuje own komunikatu — parent renderuje `<FormStatus>`).
 */
export function MfaCodeInput({
  value,
  onChange,
  onComplete,
  disabled,
  hasError,
  autoFocus,
}: {
  value: string;
  onChange: (next: string) => void;
  onComplete?: (code: string) => void;
  disabled?: boolean;
  hasError?: boolean;
  autoFocus?: boolean;
}) {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus && refs.current[0] && !disabled) {
      refs.current[0].focus();
    }
  }, [autoFocus, disabled]);

  const digits = value.padEnd(6, " ").slice(0, 6).split("");

  const commit = useCallback(
    (next: string) => {
      const sanitized = next.replace(/\D/g, "").slice(0, 6);
      onChange(sanitized);
      if (sanitized.length === 6 && onComplete) {
        onComplete(sanitized);
      }
    },
    [onChange, onComplete],
  );

  const handleInput = (idx: number, raw: string) => {
    const digit = raw.replace(/\D/g, "").slice(-1);
    const next = digits.map((d) => (d === " " ? "" : d));
    next[idx] = digit;
    const joined = next.join("").trimEnd();
    commit(joined);
    if (digit && idx < 5) {
      refs.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !e.currentTarget.value && idx > 0) {
      e.preventDefault();
      const next = digits.map((d) => (d === " " ? "" : d));
      next[idx - 1] = "";
      commit(next.join("").trimEnd());
      refs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && idx > 0) {
      e.preventDefault();
      refs.current[idx - 1]?.focus();
    }
    if (e.key === "ArrowRight" && idx < 5) {
      e.preventDefault();
      refs.current[idx + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    commit(pasted);
    const nextIdx = Math.min(pasted.length, 5);
    refs.current[nextIdx]?.focus();
  };

  return (
    <div
      className={hasError ? styles.mfaDigitsError : styles.mfaDigits}
      role="group"
      aria-label="Kod uwierzytelniający"
    >
      {[0, 1, 2, 3, 4, 5].map((idx) => (
        <input
          key={idx}
          ref={(el) => {
            refs.current[idx] = el;
          }}
          type="text"
          inputMode="numeric"
          pattern="[0-9]"
          maxLength={1}
          autoComplete={idx === 0 ? "one-time-code" : "off"}
          value={digits[idx] === " " ? "" : digits[idx]}
          disabled={disabled}
          onInput={(e) => handleInput(idx, (e.target as HTMLInputElement).value)}
          onKeyDown={(e) => handleKeyDown(idx, e)}
          onPaste={handlePaste}
          aria-label={`Cyfra ${idx + 1} z 6`}
        />
      ))}
    </div>
  );
}
